import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { get } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import PageHeader from '../../components/PageHeader';

export default function PaymentMethod() {
  const routeName = '/paymentmethods';
  const titleName = 'Convênio de pagamento';
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const id = get(params, 'id', '');
  const [companies, setCompanies] = useState([]);
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [formValues, setFormValues] = useState({
    name: '',
    data: '',
    api_key: '',
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Nome deve conter no mínimo 3 caracteres.')
      .required('Nome obrigatório.'),
    company_id: Yup.number().required('Empresa obrigatória.'),
    payment_provider_id: Yup.number().required(
      'Provedor de pagamento obrigatório.'
    ),
    payment_type_id: Yup.number().required('Tipo de pagamento obrigatório.'),
    api_key: Yup.string().required('Chave de API obrigatória.'),
    data: Yup.string().nullable(),
  });

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [fieldName]: value }));
  }, []);

  const fields = [
    {
      name: 'name',
      id: 'name',
      label: 'Nome',
      type: 'text',
      onChange: (e) => handleFieldChange('name', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'company_id',
      id: 'company_id',
      label: 'Empresa',
      type: 'select',
      options: companies,
      onChange: (e) => handleFieldChange('company_id', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'payment_provider_id',
      id: 'payment_provider_id',
      label: 'Provedor de pagamento',
      type: 'select',
      options: paymentProviders,
      onChange: (e) => handleFieldChange('payment_provider_id', e.target.value),
      colSize: 4,
      additionalProps: { className: id ? 'click-disabled' : '' },
    },
    {
      name: 'payment_type_id',
      id: 'payment_type_id',
      label: 'Tipo de pagamento',
      type: 'select',
      options: paymentTypes,
      onChange: (e) => handleFieldChange('payment_type_id', e.target.value),
      colSize: 4,
      additionalProps: { className: id ? 'click-disabled' : '' },
    },
    {
      name: 'api_key',
      id: 'api_key',
      label: 'Chave de API',
      type: 'text',
      onChange: (e) => handleFieldChange('api_key', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'data',
      id: 'data',
      label: 'Configurações do provedor',
      type: 'textarea',
      onChange: (e) => handleFieldChange('data', e.target.value),
      colSize: 8,
      additionalProps: {},
    },
  ];

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      let action = 'cadastrado';

      if (id) {
        await axios.put(`${routeName}/${id}`, formValues);
        action = 'atualizado';
      } else {
        await axios.post(routeName, formValues);
      }
      navigate('/paymentmethods');
      notification(`${titleName} ${action} com sucesso`, 'success');
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function getData() {
      try {
        setIsLoading(true);

        const optionsCompanies = [{ value: '', label: '-- Selecione --' }];
        const optionsPaymentTypes = [{ value: '', label: '-- Selecione --' }];
        const optionsPaymentProviders = [
          { value: '', label: '-- Selecione --' },
        ];
        const getCompanies = await axios.get(`/companies`);
        const getPaymentType = await axios.get(`/paymenttypes`);
        const getPaymentProviders = await axios.get(`/paymentproviders`);

        if (getCompanies) {
          getCompanies.data.forEach((data) => {
            optionsCompanies.push({
              value: data.id,
              label: data.name,
            });
          });
        }

        if (getPaymentType) {
          getPaymentType.data.forEach((data) => {
            optionsPaymentTypes.push({
              value: data.id,
              label: data.name,
            });
          });
        }

        if (getPaymentProviders) {
          getPaymentProviders.data.forEach((data) => {
            optionsPaymentProviders.push({
              value: data.id,
              label: data.name,
            });
          });
        }

        setCompanies(optionsCompanies);
        setPaymentTypes(optionsPaymentTypes);
        setPaymentProviders(optionsPaymentProviders);

        if (id) {
          const getProvider = await axios.get(`${routeName}/${id}`);
          setFormValues({
            ...getProvider.data,
          });
        }
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
        setDataLoaded(true);
      }
    }

    getData();
  }, [id, notification, navigate]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title={id ? 'Atualizar' : 'Cadastrar'}
        pages={[{ link: '/paymentmethods', name: 'Convênios de pagamentos' }]}
      />
      <PageHeader title={titleName} />
      <section>
        <Row>
          <Col lg={12}>
            <Card className="mb-5">
              <Card.Header>
                <h4 className="card-heading">
                  {id ? `Atualizar ${titleName}` : `Cadastrar ${titleName}`}
                </h4>
              </Card.Header>
              <Card.Body>
                <ErrorProcessor error={apiErrors} />
                {!id || dataLoaded ? (
                  <Forms
                    initialValues={formValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    fields={fields}
                    btnSubmit={{
                      variant: 'primary',
                      type: 'submit',
                      text: id ? 'Atualizar' : 'Cadastrar',
                    }}
                  />
                ) : null}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
